import { Injectable } from '@angular/core';

export const DEFAULT_PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100, 250];
export const DEFAULT_TABLE_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root'
})
export class TablePreferenceService {
  /**
   * Generates a namespaced, user-scoped storage key for table preferences.
   */
  public getStorageKey(tableId: string, username: string): string {
    return `lion.table.${tableId}.rows.${username}`;
  }

  /**
   * Retrieves the persisted page size for a specific table and user.
   * Falls back to `defaultRows` if the value is missing, invalid or not in `allowedOptions`.
   */
  public getPageSize(
    tableId: string,
    username: string | undefined | null,
    defaultRows: number = DEFAULT_TABLE_PAGE_SIZE,
    allowedOptions: number[] = DEFAULT_PAGE_SIZE_OPTIONS
  ): number {
    if (!username || !username.trim()) {
      return defaultRows;
    }

    try {
      const key = this.getStorageKey(tableId, username.trim());
      const rawValue = localStorage.getItem(key);

      if (rawValue === null) {
        return defaultRows;
      }

      const parsed = parseInt(rawValue, 10);
      if (Number.isInteger(parsed) && allowedOptions.includes(parsed)) {
        return parsed;
      }

      return defaultRows;
    } catch (error) {
      console.warn(`[TablePreferenceService] Failed to read page size for table '${tableId}':`, error);
      return defaultRows;
    }
  }

  /**
   * Persists the selected page size for a specific table and user into localStorage.
   */
  public setPageSize(tableId: string, username: string | undefined | null, rows: number): void {
    if (!username || !username.trim()) {
      return;
    }

    try {
      const key = this.getStorageKey(tableId, username.trim());
      localStorage.setItem(key, rows.toString());
    } catch (error) {
      console.warn(`[TablePreferenceService] Failed to save page size for table '${tableId}':`, error);
    }
  }

  /**
   * Removes the persisted page size for a specific table and user.
   */
  public clearPageSize(tableId: string, username: string | undefined | null): void {
    if (!username || !username.trim()) {
      return;
    }

    try {
      const key = this.getStorageKey(tableId, username.trim());
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`[TablePreferenceService] Failed to clear page size for table '${tableId}':`, error);
    }
  }
}
